/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* globals PKG_NAME, PKG_VERSION */

/* eslint no-unused-vars: off */

// Import global page styles (CSS reset, fonts, icons, etc.)
import Provider from "@react/react-spectrum/Provider";
import React, { PureComponent } from "react";
import ReactDOM from "react-dom";
import { providers, logging, envUtil, analytics, locale2, router, auth2 } from "dc-core";
import { dcAnalytics } from "dc-analytics-provider";
import styles from "./DCVisualizer.css";
import { loadDropinClass } from "./util/DropinUtil";
import { withErrorBoundary } from "./util/ErrorBoundary";

const launchOptions = window.adobe_dc_sdk.launchOptions;
let analyticsTimer;
const pageVisitSecondsAnalyticsMap = {
    0: "0-10",
    10: "10-30",
    30: "30-60",
    60: "60-120",
    120: "120-300",
    300: "300-600",
    600: "600+",
};

const logger = logging.getLogger("DCVisualizer");

const ORIGIN = window.location.origin;


// Initialize analytics provider
(function initAnalytics() {
    const analyticsConfig = {
        allowedKeys: {
            event: ["workflow", "subcategory", "type", "subtype", "value"],
            content: ["*"],
            source: ["name", "version", "click_context"],
            custom: ["*"],
            verb: ["*"],
            session: ["request_id"],
            user: ["user_tags"],
        },
        enableAnalytics: true,
        enableLogging: true,
        serverEnv: envUtil.getEnvVar("server_env"),
        saveURLInfo: true,
        flags: ["dc-visualizer-"],
        appName: envUtil.getEnvVar("app_identifier") || PKG_NAME,
        appVersion: PKG_VERSION,
    };
    analyticsTimer = setInterval(() => {
        // eslint-disable-next-line no-underscore-dangle
        if (window.__satelliteLoadedPromise) {
            clearInterval(analyticsTimer);
            // eslint-disable-next-line no-underscore-dangle
            window.__satelliteLoadedPromise.then(() => {
                dcAnalytics.initialize(analyticsConfig, logging, undefined, analytics, locale2);
            });
        }
    }, 1000);
}());

class DCVisualizer extends PureComponent {
    constructor() {
        super();
        this.state = {
            visualizerContainerDropinCls: null,
        };
        // Use stage dcapi uri for local
        const dcApiOrigin = ORIGIN.includes("local-test") ?
            "https://dc-api-dev.adobe.io" : envUtil.getEnvVar("dcapi_uri");
        const dcConfiguration = {
            identityAccess: auth2,
            "x-api-app-info": "virgo-dropin-harness",
            discovery: {
                uri: `${dcApiOrigin}/discovery`,
                accept: "application/vnd.adobe.dc+json;"
                  + `profile="${dcApiOrigin}/schemas/discovery_v1.json"`,
            },
        };
    }

    componentDidMount() {
        Promise.all([loadDropinClass("visualizer-container")])
            .then(results => {
                this.setState({
                    visualizerContainerDropinCls: results[0],
                });
            });
    }

    getTypekitId = appLocale => {
        const typekitIds = {
            "ja-jp": "pcg0npo",
            "ko-kr": "wmm2qek",
            "zh-cn": "giv3kdk",
            "zh-tw": "bvi5ncj",
        };
        return typekitIds[appLocale] || "bxf0ivf";
    }

    render() {
        const VisualizerContainerDropinCls = this.state.visualizerContainerDropinCls;
        const appLocale = locale2.getLocale();
        const typekitId = this.getTypekitId(appLocale.toLowerCase());
        return VisualizerContainerDropinCls ? (
            <Provider
                typekitId={ typekitId }
                locale={ appLocale }
                toastPlacement="bottom"
                overflow="hidden"
            >
                <VisualizerContainerDropinCls verb={ launchOptions.verb } />
            </Provider>
        ) : null;
    }
}

const DCVisualizerWithErrorBoundary = withErrorBoundary(DCVisualizer, "dc-visualizer");

export default DCVisualizerWithErrorBoundary;

ReactDOM.render(<DCVisualizerWithErrorBoundary />, document.getElementById("root"));

// Analytics for page visit duration
window.addEventListener("load", () => {
    const startTime = new Date();
    window.addEventListener("beforeunload", () => {
        const reverseSortInteger = (a, b) => b - a;
        const endTime = new Date();
        const pageVisitSeconds = (endTime - startTime) / 1000;
        // Determine which "slot" of duration analytics we fall into and log it
        Object.keys(pageVisitSecondsAnalyticsMap).sort(reverseSortInteger).every(limit => {
            if (pageVisitSeconds > limit) {
                const eventSender = analytics.getEventSender("Visualizer");
                if (eventSender) {
                    eventSender({
                        event: {
                            workflow: "Visualizer",
                            subcategory: "Session",
                            type: "TimeRange",
                            subtype: pageVisitSecondsAnalyticsMap[limit],
                        },
                    });
                }
                return false; // exit loop
            }
            return true;
        });
    });
});
